import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from '@env';
import { contextoSinAutorizacion } from '@nucleo/http/contexto-peticion-util';
import { SesionServicio } from './sesion-servicio';
import {
  SolicitudCodigoIngresoDto,
  SolicitudInicioSesionDto,
  SolicitudInicioSesionPinDto,
  SolicitudTokenRenovacionDto,
  SolicitudVerificacionCodigoDto,
  TokenRespuestaDto,
} from './token-respuesta-dto';
import {
  SolicitudCambioContrasenaDto,
  SolicitudCambioIdiomaDto,
  SolicitudCambioPinDto,
  UsuarioActualDto,
} from './usuario-actual-dto';
import { IdiomaServicio } from '@nucleo/i18n/idioma-servicio';
import { Idioma, esIdiomaValido } from '@nucleo/i18n/idioma.enum';

const RUTA_INICIAR_SESION = '/auth/login';
const RUTA_INICIAR_SESION_PIN = '/auth/login-pin';
const RUTA_SOLICITAR_CODIGO = '/auth/otp/request';
const RUTA_VERIFICAR_CODIGO = '/auth/otp/verify';
const RUTA_RENOVAR_SESION = '/auth/refresh';
const RUTA_CERRAR_SESION = '/auth/logout';
const RUTA_USUARIO_ACTUAL = '/account/me';
const RUTA_CAMBIAR_CONTRASENA = '/account/password';
const RUTA_CAMBIAR_PIN = '/account/pin';
const RUTA_CAMBIAR_IDIOMA = '/account/language';

/**
 * Llamadas HTTP del ciclo de vida de la sesión (`/auth/*` y `/account/me|password`).
 * Vive en `nucleo` porque la usan el interceptor (renovación), los guardianes, el layout y las
 * características `autenticacion` y `mi-perfil`. Contratos: `documentacion/apis/autenticacion-api.md`
 * y `cuenta-api.md`.
 */
@Injectable({ providedIn: 'root' })
export class SesionApiServicio {
  private readonly http = inject(HttpClient);
  private readonly servicioSesion = inject(SesionServicio);
  private readonly servicioIdioma = inject(IdiomaServicio);
  private readonly urlBase = environment.apisBase.appWebComunia.url;

  /** Renovación en curso: todas las peticiones que la necesiten esperan la misma (el refresh rota el token). */
  private renovacionEnCurso$: Observable<void> | null = null;

  /** Inicia sesión y carga el usuario actual. Con contraseña temporal el usuario viene sin permisos. */
  iniciarSesion(solicitud: SolicitudInicioSesionDto): Observable<UsuarioActualDto> {
    return this.abrirSesion(RUTA_INICIAR_SESION, solicitud);
  }

  /** Inicia sesión con usuario o correo y PIN de 6 dígitos (`POST /auth/login-pin`). */
  iniciarSesionConPin(solicitud: SolicitudInicioSesionPinDto): Observable<UsuarioActualDto> {
    return this.abrirSesion(RUTA_INICIAR_SESION_PIN, solicitud);
  }

  /** Pide que se envíe un código de ingreso al correo de la cuenta. El backend responde igual exista o no la cuenta. */
  solicitarCodigoIngreso(usuario: string): Observable<void> {
    const solicitud: SolicitudCodigoIngresoDto = { username: usuario };
    return this.http.post<void>(`${this.urlBase}${RUTA_SOLICITAR_CODIGO}`, solicitud, {
      context: contextoSinAutorizacion(),
    });
  }

  /** Inicia sesión con el código recibido por correo (`POST /auth/otp/verify`; de un solo uso). */
  iniciarSesionConCodigo(solicitud: SolicitudVerificacionCodigoDto): Observable<UsuarioActualDto> {
    return this.abrirSesion(RUTA_VERIFICAR_CODIGO, solicitud);
  }

  /** Crea (con la contraseña actual) o cambia (con el PIN actual) el PIN propio (`PUT /account/pin`). */
  guardarPin(solicitud: SolicitudCambioPinDto): Observable<void> {
    return this.http.put<void>(`${this.urlBase}${RUTA_CAMBIAR_PIN}`, solicitud);
  }

  /** Pide tokens a una ruta pública de `/auth/*`, reemplaza la sesión anterior y carga el usuario actual. */
  private abrirSesion(ruta: string, solicitud: object): Observable<UsuarioActualDto> {
    return this.http
      .post<TokenRespuestaDto>(`${this.urlBase}${ruta}`, solicitud, {
        context: contextoSinAutorizacion(),
      })
      .pipe(
        tap(() => this.servicioSesion.limpiar()),
        tap((tokens) => this.servicioSesion.guardarTokens(tokens)),
        switchMap(() => this.cargarUsuarioActual()),
      );
  }

  /** Renueva el access token con el refresh token (rotación) y refresca permisos del usuario. */
  renovar(): Observable<void> {
    if (!this.renovacionEnCurso$) {
      const solicitud: SolicitudTokenRenovacionDto = {
        refreshToken: this.servicioSesion.tokenRenovacion(),
      };
      this.renovacionEnCurso$ = this.http
        .post<TokenRespuestaDto>(`${this.urlBase}${RUTA_RENOVAR_SESION}`, solicitud, {
          context: contextoSinAutorizacion(),
        })
        .pipe(
          tap((tokens) => this.servicioSesion.guardarTokens(tokens)),
          switchMap(() => this.cargarUsuarioActual()),
          map(() => undefined),
          finalize(() => (this.renovacionEnCurso$ = null)),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
    return this.renovacionEnCurso$;
  }

  cargarUsuarioActual(): Observable<UsuarioActualDto> {
    return this.http
      .get<UsuarioActualDto>(`${this.urlBase}${RUTA_USUARIO_ACTUAL}`)
      .pipe(
        tap((usuario) => {
          this.servicioSesion.establecerUsuario(usuario);
          // El idioma guardado en el usuario manda: lo sigue en cualquier equipo.
          if (esIdiomaValido(usuario.preferredLanguage)) {
            this.servicioIdioma.cambiar(usuario.preferredLanguage);
          }
        }),
      );
  }

  /**
   * Cambia el idioma de la aplicación al instante y, si hay sesión, lo guarda en el usuario
   * (`PUT /account/language`). Si no se puede guardar, el cambio igual queda en este navegador.
   */
  cambiarIdioma(idioma: Idioma): Observable<void> {
    this.servicioIdioma.cambiar(idioma);
    if (!this.servicioSesion.estaAutenticado()) {
      return of(undefined);
    }
    const solicitud: SolicitudCambioIdiomaDto = { language: idioma };
    return this.http
      .put<void>(`${this.urlBase}${RUTA_CAMBIAR_IDIOMA}`, solicitud)
      .pipe(
        tap(() => {
          const usuario = this.servicioSesion.usuario();
          if (usuario) this.servicioSesion.establecerUsuario({ ...usuario, preferredLanguage: idioma });
        }),
      );
  }

  /**
   * Cambia la contraseña propia. El backend cierra todas las sesiones del usuario, así que a continuación
   * se vuelve a iniciar sesión con la contraseña nueva para que el usuario no tenga que escribirla otra vez.
   */
  cambiarContrasenaYReingresar(
    contrasenaActual: string,
    contrasenaNueva: string,
  ): Observable<UsuarioActualDto> {
    const usuario = this.servicioSesion.usuario()?.username ?? '';
    const solicitud: SolicitudCambioContrasenaDto = {
      currentPassword: contrasenaActual,
      newPassword: contrasenaNueva,
    };
    return this.http
      .put<void>(`${this.urlBase}${RUTA_CAMBIAR_CONTRASENA}`, solicitud)
      .pipe(switchMap(() => this.iniciarSesion({ username: usuario, password: contrasenaNueva })));
  }

  /** Revoca el refresh token en el backend (siempre responde 204) y limpia la sesión local pase lo que pase. */
  cerrarSesion(): Observable<void> {
    const tokenRenovacion = this.servicioSesion.tokenRenovacion();
    if (!tokenRenovacion) {
      this.servicioSesion.limpiar();
      return of(undefined);
    }
    const solicitud: SolicitudTokenRenovacionDto = { refreshToken: tokenRenovacion };
    return this.http
      .post<void>(`${this.urlBase}${RUTA_CERRAR_SESION}`, solicitud, {
        context: contextoSinAutorizacion(),
      })
      .pipe(
        catchError(() => of(undefined)),
        finalize(() => this.servicioSesion.limpiar()),
      );
  }
}
